import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Copy,
  KeyRound,
  Loader2,
  Mail,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { FormSection } from '@/components/forms/Form';
import { FormDialog } from '@/components/forms/FormDialog';
import { FormField, FormSelect } from '@/components/forms/FormField';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useUpdateUserMeta, useUsersList, type UserListRow } from '@/hooks/queries/useUsersList';
import { useAllPerimetres } from '@/hooks/queries/usePerimetres';
import { useSetUserEntreprises, useUserEntreprises } from '@/hooks/queries/useUserEntreprises';
import { supabase } from '@/lib/supabase';
import { useRole } from '@/hooks/useRole';
import { useUser } from '@/hooks/useUser';
import { ROLE_LABELS } from '@/lib/auth';
import { initials } from '@/lib/format';
import { t } from '@/locales/i18n';
import { cn } from '@/lib/utils';
import type { RoleType } from '@/lib/database.types';

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as RoleType[]).map((r) => ({
  value: r,
  label: ROLE_LABELS[r],
}));

const editSchema = z.object({
  full_name: z.string().min(2),
  role: z.enum([
    'super_admin',
    'direction',
    'responsable_activite',
    'commercial',
    'comptable',
    'formateur',
    'responsable_rh',
    'responsable_marketing',
  ]),
  entreprise_id: z.string().uuid().nullable(),
  activites_ids: z.array(z.string().uuid()),
});

type EditForm = z.infer<typeof editSchema>;

export function UtilisateursPage(): JSX.Element {
  const { isAdmin } = useRole();
  const { data: users = [], isLoading, refetch } = useUsersList();
  const { data: entreprises = [] } = useEntreprises();
  const { data: allActivites = [] } = useAllActivites();
  const { data: allPerimetres = [] } = useAllPerimetres();
  const updateMeta = useUpdateUserMeta();
  const setUserEntreprises = useSetUserEntreprises();

  const perimetreSummary = useMemo(() => {
    const m = new Map<string, string>();
    const byUser = new Map<string, typeof allPerimetres>();
    for (const p of allPerimetres) {
      if (!byUser.has(p.user_id)) byUser.set(p.user_id, []);
      byUser.get(p.user_id)!.push(p);
    }
    for (const [uid, perimetres] of byUser) {
      // Compte d'activités distinctes + nb sous-segments
      const activeMap = new Map<string, { all: boolean; segs: number }>();
      for (const p of perimetres) {
        const cur = activeMap.get(p.activite_id) ?? { all: false, segs: 0 };
        if (p.sous_segment_id === null) cur.all = true;
        else cur.segs += 1;
        activeMap.set(p.activite_id, cur);
      }
      const labels: string[] = [];
      for (const [actId, info] of activeMap) {
        const a = allActivites.find((x) => x.id === actId);
        if (!a) continue;
        labels.push(info.all ? `${a.nom} (tous)` : `${a.nom} — ${info.segs} segment(s)`);
      }
      m.set(uid, labels.join(' · ') || '—');
    }
    return m;
  }, [allPerimetres, allActivites]);

  const { user: currentUser } = useUser();
  const [dialog, setDialog] = useState<{ open: boolean; user: UserListRow | null }>({
    open: false,
    user: null,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<UserListRow | null>(null);
  const [resetTarget, setResetTarget] = useState<UserListRow | null>(null);
  const [resetGenerated, setResetGenerated] = useState<{ email: string; password: string } | null>(
    null,
  );
  const [actionPending, setActionPending] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    if (selectedIds.size === users.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(users.map((u) => u.user_id)));
  };

  const callAdminAction = async (body: {
    action: 'delete' | 'reset_password_email' | 'reset_password_generate';
    target_user_id?: string;
    target_email?: string;
  }): Promise<{ ok?: boolean; new_password?: string; error?: string }> => {
    const { data, error } = await supabase.functions.invoke('admin-user-actions', { body });
    if (error || (data && (data as { error?: string }).error)) {
      throw new Error(error?.message ?? (data as { error?: string }).error ?? 'inconnu');
    }
    return data as { ok?: boolean; new_password?: string };
  };

  const handleDelete = async (u: UserListRow): Promise<void> => {
    setActionPending(true);
    try {
      await callAdminAction({ action: 'delete', target_user_id: u.user_id });
      toast.success(`Utilisateur ${u.full_name ?? u.user_id.slice(0, 8)} supprimé`);
      void refetch();
      setConfirmDelete(null);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    } finally {
      setActionPending(false);
    }
  };

  const handleResetEmail = async (u: UserListRow): Promise<void> => {
    setActionPending(true);
    try {
      // Récupère email depuis auth via rpc-friendly fallback : on l'a probablement dans u
      const email = (u as unknown as { email?: string }).email;
      if (!email) {
        toast.error('Email utilisateur introuvable dans la fiche');
        return;
      }
      await callAdminAction({ action: 'reset_password_email', target_email: email });
      toast.success(`Email reset envoyé à ${email}`);
      setResetTarget(null);
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    } finally {
      setActionPending(false);
    }
  };

  const handleResetGenerate = async (u: UserListRow): Promise<void> => {
    setActionPending(true);
    try {
      const result = await callAdminAction({
        action: 'reset_password_generate',
        target_user_id: u.user_id,
      });
      if (result.new_password) {
        const email = (u as unknown as { email?: string }).email ?? u.full_name ?? u.user_id;
        setResetGenerated({ email, password: result.new_password });
        setResetTarget(null);
      }
    } catch (e) {
      toast.error(`Échec : ${(e as Error).message}`);
    } finally {
      setActionPending(false);
    }
  };

  const handleBulkDelete = async (): Promise<void> => {
    setActionPending(true);
    let ok = 0;
    const errs: string[] = [];
    for (const id of Array.from(selectedIds)) {
      if (id === currentUser?.id) {
        errs.push('Soi-même ignoré');
        continue;
      }
      try {
        await callAdminAction({ action: 'delete', target_user_id: id });
        ok++;
      } catch (e) {
        errs.push((e as Error).message);
      }
    }
    if (errs.length === 0) toast.success(`${ok} utilisateur(s) supprimé(s)`);
    else toast.warning(`${ok} OK · ${errs.length} échec(s) — ex: ${errs[0]}`);
    setSelectedIds(new Set());
    void refetch();
    setActionPending(false);
  };

  const handleBulkResetEmail = async (): Promise<void> => {
    setActionPending(true);
    let ok = 0;
    for (const id of Array.from(selectedIds)) {
      const u = users.find((x) => x.user_id === id);
      const email = (u as unknown as { email?: string } | undefined)?.email;
      if (!email) continue;
      try {
        await callAdminAction({ action: 'reset_password_email', target_email: email });
        ok++;
      } catch {
        /* skip */
      }
    }
    toast.success(`${ok} email(s) de réinitialisation envoyé(s)`);
    setSelectedIds(new Set());
    setActionPending(false);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('parametres.utilisateurs.title')}
        subtitle={t('parametres.utilisateurs.subtitle')}
        actions={
          isAdmin && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              {t('parametres.utilisateurs.add')}
            </Button>
          )
        }
      />
      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
          </div>
        ) : users.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            Aucun utilisateur visible (RLS).
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  {isAdmin && (
                    <th className="px-3 py-2 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === users.length && users.length > 0}
                        onChange={toggleAll}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left">Utilisateur</th>
                  <th className="px-3 py-2 text-left">Rôle</th>
                  <th className="px-3 py-2 text-left">Entreprise</th>
                  <th className="px-3 py-2 text-left">Activités rattachées</th>
                  <th className="px-3 py-2 text-left">Périmètre CRM</th>
                  <th className="px-3 py-2 text-right">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const ent = entreprises.find((e) => e.id === u.entreprise_id);
                  const acts = allActivites.filter((a) => u.activites_ids.includes(a.id));
                  return (
                    <tr
                      key={u.user_id}
                      className="border-b border-border last:border-0 hover:bg-muted/20"
                    >
                      {isAdmin && (
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(u.user_id)}
                            onChange={() => toggleSelect(u.user_id)}
                            aria-label={`Sélectionner ${u.full_name ?? u.user_id}`}
                          />
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                            {initials(u.full_name)}
                          </div>
                          <div>
                            <div className="font-medium">{u.full_name ?? 'Sans nom'}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">
                              {u.user_id.slice(0, 8)}…
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge>{ROLE_LABELS[u.role]}</Badge>
                      </td>
                      <td className="px-3 py-2 text-xs">{ent?.nom ?? '—'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {acts.length === 0 && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                          {acts.slice(0, 4).map((a) => (
                            <Badge key={a.id} variant="outline" className="text-[10px]">
                              {a.nom}
                            </Badge>
                          ))}
                          {acts.length > 4 && (
                            <span className="text-xs text-muted-foreground">
                              +{acts.length - 4}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground">
                        <span className="line-clamp-2">
                          {perimetreSummary.get(u.user_id) ?? '—'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isAdmin && (
                          <div className="flex items-center justify-end gap-1">
                            <Button asChild variant="ghost" size="sm" title="Périmètre CRM">
                              <Link to={`/parametres/utilisateurs/${u.user_id}/perimetre`}>
                                <ShieldCheck className="h-3.5 w-3.5" /> Périmètre
                              </Link>
                            </Button>
                            <Button
                              asChild
                              variant="ghost"
                              size="sm"
                              title="Permissions par feature"
                            >
                              <Link to={`/parametres/utilisateurs/${u.user_id}/permissions`}>
                                <ShieldCheck className="h-3.5 w-3.5" /> Permissions
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDialog({ open: true, user: u })}
                              title="Modifier"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setResetTarget(u)}
                              title="Réinitialiser mot de passe"
                            >
                              <KeyRound className="h-3.5 w-3.5" />
                            </Button>
                            {u.user_id !== currentUser?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setConfirmDelete(u)}
                                title="Supprimer"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <UserEditDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
        user={dialog.user}
        entreprises={entreprises}
        allActivites={allActivites}
        onSubmit={async (v, extraEntreprisesIds) => {
          if (!dialog.user) return;
          await updateMeta.mutateAsync({ user_id: dialog.user.user_id, ...v });
          // Sprint 8.0 — Sync table user_entreprises_acces (entreprise principale
          // toujours incluse pour cohérence avec has_entreprise_access).
          const merged = new Set(extraEntreprisesIds);
          if (v.entreprise_id) merged.add(v.entreprise_id);
          await setUserEntreprises.mutateAsync({
            user_id: dialog.user.user_id,
            entreprises_ids: [...merged],
          });
          toast.success('Utilisateur mis à jour');
          setDialog({ open: false, user: null });
        }}
      />

      <UserCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        entreprises={entreprises}
        allActivites={allActivites}
        onCreated={() => {
          void refetch();
          setCreateOpen(false);
        }}
      />

      {/* Sprint 6.9.5 — Sticky bulk bar */}
      {selectedIds.size > 0 && isAdmin && (
        <div className="fixed bottom-4 left-1/2 z-30 flex -translate-x-1/2 flex-wrap items-center gap-2 rounded-xl border border-zinc-300 bg-zinc-50/95 px-4 py-2.5 shadow-lg backdrop-blur">
          <span className="text-sm font-bold text-zinc-900">
            {selectedIds.size} utilisateur{selectedIds.size > 1 ? 's' : ''} sélectionné
            {selectedIds.size > 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={() => void handleBulkResetEmail()}
            disabled={actionPending}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-blue-600 px-3 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Mail className="h-3.5 w-3.5" /> Reset password (email)
          </button>
          <button
            type="button"
            onClick={() => void handleBulkDelete()}
            disabled={actionPending}
            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            <Trash2 className="h-3.5 w-3.5" /> Supprimer
          </button>
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-slate-500 hover:bg-slate-100"
          >
            <XCircle className="h-3 w-3" /> Désélectionner
          </button>
        </div>
      )}

      {/* Confirm Delete single */}
      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={
          confirmDelete
            ? `Supprimer ${confirmDelete.full_name ?? 'cet utilisateur'} ?`
            : 'Supprimer ?'
        }
        description="Action irréversible. Toutes ses sessions seront invalidées. Le système refuse si c'est le dernier super_admin."
        loading={actionPending}
        onConfirm={async () => {
          if (confirmDelete) await handleDelete(confirmDelete);
        }}
      />

      {/* Reset password options dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Réinitialiser le mot de passe · {resetTarget?.full_name ?? '—'}
            </DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <button
              type="button"
              onClick={() => resetTarget && void handleResetEmail(resetTarget)}
              disabled={actionPending}
              className="flex w-full items-start gap-3 rounded-xl border-2 border-blue-200 bg-blue-50/50 p-4 text-left transition-all hover:border-blue-400 disabled:opacity-60"
            >
              <Mail className="mt-1 h-5 w-5 shrink-0 text-blue-600" />
              <div>
                <div className="font-semibold text-blue-900">
                  Envoyer un email de réinitialisation
                </div>
                <div className="mt-0.5 text-xs text-blue-700">
                  L&apos;utilisateur reçoit un lien pour définir lui-même son nouveau mot de passe.
                  Méthode recommandée.
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => resetTarget && void handleResetGenerate(resetTarget)}
              disabled={actionPending}
              className="flex w-full items-start gap-3 rounded-xl border-2 border-amber-200 bg-amber-50/50 p-4 text-left transition-all hover:border-amber-400 disabled:opacity-60"
            >
              <KeyRound className="mt-1 h-5 w-5 shrink-0 text-amber-600" />
              <div>
                <div className="font-semibold text-amber-900">Générer un nouveau mot de passe</div>
                <div className="mt-0.5 text-xs text-amber-700">
                  Génère un mot de passe aléatoire 16 caractères. Affiché UNE SEULE FOIS dans la
                  modale suivante. À transmettre en personne / canal sécurisé.
                </div>
              </div>
            </button>
          </DialogBody>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setResetTarget(null)}
              className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              Annuler
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Display generated password ONCE */}
      <Dialog open={!!resetGenerated} onOpenChange={(o) => !o && setResetGenerated(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mot de passe généré · {resetGenerated?.email}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-4 text-center">
              <div className="font-mono text-xl font-bold tracking-wider text-amber-900">
                {resetGenerated?.password}
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (resetGenerated) {
                  await navigator.clipboard.writeText(resetGenerated.password);
                  toast.success('Copié dans le presse-papier');
                }
              }}
              className={cn(
                'inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-zinc-800 px-4 text-sm font-semibold text-white hover:bg-zinc-900',
              )}
            >
              <Copy className="h-4 w-4" /> Copier
            </button>
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              ⚠ Ce mot de passe ne sera <strong>plus jamais affiché</strong>. Transmettez-le par
              canal sécurisé. L&apos;ancien mot de passe est invalidé immédiatement.
            </div>
          </DialogBody>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setResetGenerated(null)}
              className="inline-flex h-10 items-center justify-center rounded-xl bg-zinc-800 px-4 text-sm font-semibold text-white hover:bg-zinc-900"
            >
              J&apos;ai pris note du mot de passe
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface UserEditDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  user: UserListRow | null;
  entreprises: { id: string; nom: string }[];
  allActivites: { id: string; entreprise_id: string; nom: string }[];
  onSubmit: (values: EditForm, extraEntreprisesIds: string[]) => Promise<void>;
}

function UserEditDialog({
  open,
  onOpenChange,
  user,
  entreprises,
  allActivites,
  onSubmit,
}: UserEditDialogProps): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    defaultValues: { full_name: '', role: 'commercial', entreprise_id: null, activites_ids: [] },
  });

  // Sprint 8.0 — Multi-entreprises : on charge les accès en plus de l'entreprise principale.
  const { data: userEntreprises = [] } = useUserEntreprises(open && user ? user.user_id : null);
  const [extraEntreprisesIds, setExtraEntreprisesIds] = useState<string[]>([]);

  useEffect(() => {
    if (open && user) {
      reset({
        full_name: user.full_name ?? '',
        role: user.role,
        entreprise_id: user.entreprise_id,
        activites_ids: user.activites_ids,
      });
    }
  }, [open, user, reset]);

  // Sync l'état local avec les accès chargés (hors entreprise principale, qui est gérée par le form).
  useEffect(() => {
    if (open && user) {
      const principal = user.entreprise_id;
      setExtraEntreprisesIds(
        userEntreprises.map((ue) => ue.entreprise_id).filter((id) => id !== principal),
      );
    }
  }, [open, user, userEntreprises]);

  const entId = watch('entreprise_id');
  const ids = watch('activites_ids');
  const scoped = allActivites.filter((a) => a.entreprise_id === entId);
  const extrasDisponibles = entreprises.filter((e) => e.id !== entId);
  const allExtrasSelected =
    extrasDisponibles.length > 0 &&
    extrasDisponibles.every((e) => extraEntreprisesIds.includes(e.id));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('parametres.utilisateurs.edit')}
      description="Modifie le rôle et le rattachement (entreprise + activités)."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        await onSubmit(v, extraEntreprisesIds);
      })}
    >
      <FormSection>
        <FormField
          label={t('parametres.utilisateurs.form.fullName')}
          required
          {...register('full_name')}
          error={errors.full_name?.message}
          fullWidth
        />
        <FormSelect
          label={t('parametres.utilisateurs.form.role')}
          required
          value={watch('role')}
          onChange={(v) => setValue('role', v as RoleType, { shouldValidate: true })}
          options={ROLE_OPTIONS}
        />
        <FormSelect
          label={t('parametres.utilisateurs.form.entreprise')}
          value={entId ?? ''}
          onChange={(v) => {
            setValue('entreprise_id', v || null);
            setValue('activites_ids', []);
          }}
          options={[
            { value: '', label: '— Aucune —' },
            ...entreprises.map((e) => ({ value: e.id, label: e.nom })),
          ]}
        />
      </FormSection>
      <FormSection
        title={t('parametres.utilisateurs.form.activites')}
        description="Cliquez pour activer/désactiver une activité."
      >
        <div className="sm:col-span-2">
          {scoped.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Sélectionnez d&apos;abord une entreprise.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {scoped.map((a) => {
                const active = ids.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() =>
                      setValue(
                        'activites_ids',
                        active ? ids.filter((x) => x !== a.id) : [...ids, a.id],
                      )
                    }
                    className={
                      'rounded-md border px-2 py-1 text-xs transition-colors ' +
                      (active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:text-foreground')
                    }
                  >
                    {a.nom}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </FormSection>

      {/* Sprint 8.0 — Accès supplémentaires multi-entreprises */}
      <FormSection
        title="Accès à d'autres entreprises"
        description="Au-delà de l'entreprise principale, à quelles autres entreprises ce user a-t-il accès ?"
      >
        <div className="space-y-2 sm:col-span-2">
          {extrasDisponibles.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Aucune autre entreprise disponible (ou entreprise principale non sélectionnée).
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setExtraEntreprisesIds(
                      allExtrasSelected ? [] : extrasDisponibles.map((e) => e.id),
                    );
                  }}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-50"
                >
                  {allExtrasSelected ? 'Tout décocher' : 'Tout sélectionner'}
                </button>
                <span className="text-xs text-muted-foreground">
                  {extraEntreprisesIds.length} / {extrasDisponibles.length} sélectionnée
                  {extraEntreprisesIds.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {extrasDisponibles.map((e) => {
                  const active = extraEntreprisesIds.includes(e.id);
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() =>
                        setExtraEntreprisesIds((cur) =>
                          active ? cur.filter((x) => x !== e.id) : [...cur, e.id],
                        )
                      }
                      className={
                        'rounded-md border px-2 py-1 text-xs transition-colors ' +
                        (active
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background hover:text-foreground')
                      }
                    >
                      {e.nom}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </FormSection>
    </FormDialog>
  );
}

interface UserCreateDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  entreprises: { id: string; nom: string }[];
  allActivites: { id: string; entreprise_id: string; nom: string }[];
  onCreated: () => void;
}

const createSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Min. 8 caractères'),
  full_name: z.string().min(2),
  role: z.enum([
    'super_admin',
    'direction',
    'responsable_activite',
    'commercial',
    'comptable',
    'formateur',
    'responsable_rh',
    'responsable_marketing',
  ]),
  entreprise_id: z.string().uuid().nullable(),
  activites_ids: z.array(z.string().uuid()),
});
type CreateForm = z.infer<typeof createSchema>;

function UserCreateDialog({
  open,
  onOpenChange,
  entreprises,
  allActivites,
  onCreated,
}: UserCreateDialogProps): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      email: '',
      password: '',
      full_name: '',
      role: 'commercial',
      entreprise_id: null,
      activites_ids: [],
    },
  });

  useEffect(() => {
    if (open)
      reset({
        email: '',
        password: '',
        full_name: '',
        role: 'commercial',
        entreprise_id: null,
        activites_ids: [],
      });
  }, [open, reset]);

  const entId = watch('entreprise_id');
  const ids = watch('activites_ids');
  const scoped = allActivites.filter((a) => a.entreprise_id === entId);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('parametres.utilisateurs.add')}
      description="Création via Edge Function (Supabase Auth Admin). E-mail confirmé automatiquement."
      isSubmitting={isSubmitting}
      onSubmit={handleSubmit(async (v) => {
        const { data, error } = await supabase.functions.invoke('create-user', { body: v });
        if (error || (data && (data as { error?: string }).error)) {
          toast.error(
            'Échec : ' + (error?.message ?? (data as { error?: string }).error ?? 'inconnu'),
          );
          return;
        }
        toast.success('Utilisateur créé : ' + v.email);
        onCreated();
      })}
    >
      <FormSection title="Identifiants">
        <FormField
          label={t('parametres.utilisateurs.form.fullName')}
          required
          {...register('full_name')}
          error={errors.full_name?.message}
        />
        <FormField
          type="email"
          label={t('parametres.utilisateurs.form.email')}
          required
          {...register('email')}
          error={errors.email?.message}
        />
        <FormField
          type="password"
          label={t('parametres.utilisateurs.form.password')}
          required
          {...register('password')}
          error={errors.password?.message}
          hint="Min. 8 caractères. L'utilisateur pourra le changer."
          fullWidth
        />
      </FormSection>
      <FormSection title="Rôle & rattachement">
        <FormSelect
          label={t('parametres.utilisateurs.form.role')}
          required
          value={watch('role')}
          onChange={(v) => setValue('role', v as RoleType, { shouldValidate: true })}
          options={ROLE_OPTIONS}
        />
        <FormSelect
          label={t('parametres.utilisateurs.form.entreprise')}
          value={entId ?? ''}
          onChange={(v) => {
            setValue('entreprise_id', v || null);
            setValue('activites_ids', []);
          }}
          options={[
            { value: '', label: '— Aucune —' },
            ...entreprises.map((e) => ({ value: e.id, label: e.nom })),
          ]}
        />
        <div className="space-y-1.5 sm:col-span-2">
          <label className="text-sm font-medium">
            {t('parametres.utilisateurs.form.activites')}
          </label>
          <div className="flex flex-wrap gap-1.5">
            {scoped.length === 0 ? (
              <span className="text-xs text-muted-foreground">
                Sélectionnez d&apos;abord une entreprise.
              </span>
            ) : (
              scoped.map((a) => {
                const active = ids.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() =>
                      setValue(
                        'activites_ids',
                        active ? ids.filter((x) => x !== a.id) : [...ids, a.id],
                      )
                    }
                    className={
                      'rounded-md border px-2 py-1 text-xs transition-colors ' +
                      (active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-background hover:text-foreground')
                    }
                  >
                    {a.nom}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </FormSection>
    </FormDialog>
  );
}
